import { Loader2 } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { useAuthStore } from '@/stores/authStore';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { navForRole, groupForPath, pathMatches, type Role } from '@/types/navigation';
import { TransitionLink, useNavSpinner } from './NavTransition';

/**
 * The one layout for every multi-page nav group (Administration / Organization /
 * Research / Bots). It reads the nav registry, finds the group that owns the
 * current route, and renders that group's tab strip + optional group toolbar
 * (e.g. the org selector) above an <Outlet/>. Because it's derived from the
 * registry, the tabs never "disappear" when you click into a child page, and
 * adding/reordering a page is a one-line registry edit — no per-view tab bars.
 */
export function TabShell() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  const botNav = useGrantSlot('botAccess') !== 'hidden';
  const { pathname } = useLocation();
  const spinnerPath = useNavSpinner();
  const group = groupForPath(navForRole(role, botNav), pathname);
  const pages = group?.pages ?? [];
  const showTabs = pages.length > 1;
  const showToolbar = group?.toolbar === 'org';

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {(showTabs || showToolbar) && (
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 pt-3">
          <div className="flex items-center gap-1">
            {showTabs &&
              pages.map((p) => {
                const active = pathMatches(pathname, p.path);
                return (
                  <TransitionLink
                    key={p.path}
                    to={p.path}
                    className={cn(
                      '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm transition-colors',
                      active
                        ? 'border-primary text-foreground font-medium'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {/* Tabs have no icon to swap, so the spinner gets a slot that
                        is always there — otherwise showing it would shove the
                        label sideways under the cursor mid-navigation. */}
                    <span className="flex w-3.5 shrink-0 justify-center">
                      {spinnerPath === p.path && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    </span>
                    {p.label}
                    {p.badge && (
                      <span className="bg-muted-foreground/20 text-muted-foreground rounded px-1 text-[8px] font-semibold uppercase leading-tight">
                        {p.badge}
                      </span>
                    )}
                  </TransitionLink>
                );
              })}
          </div>
          {showToolbar && <OrgSwitcher className="pb-1.5" />}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
        <Outlet />
      </div>
    </div>
  );
}
